import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { SUBJECTS, SUBJECT_COLORS, formatPercentage, cn } from '../lib/utils';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Legend
} from 'recharts';
import {
    Target,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    XCircle,
    BarChart3
} from 'lucide-react';
import Layout from '../components/Layout';

export default function Analytics() {
    const [stats, setStats] = useState(null);
    const [patterns, setPatterns] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [statsRes, patternsRes] = await Promise.all([
                analyticsAPI.getPerformance(),
                analyticsAPI.getMissedPatterns()
            ]);
            setStats(statsRes.data);
            setPatterns(patternsRes.data.patterns);
        } catch (err) {
            console.error('Failed to fetch analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    // Prepare radar chart data
    const radarData = stats ? SUBJECTS.map(subject => ({
        subject: subject.substring(0, 4),
        fullName: subject,
        accuracy: stats.by_subject[subject]?.accuracy || 0,
        questions: stats.by_subject[subject]?.total || 0
    })) : [];

    // Prepare difficulty comparison data
    const difficultyData = stats ? [
        { name: 'Easy', accuracy: stats.by_difficulty.easy?.accuracy || 0, total: stats.by_difficulty.easy?.total || 0 },
        { name: 'Medium', accuracy: stats.by_difficulty.medium?.accuracy || 0, total: stats.by_difficulty.medium?.total || 0 },
        { name: 'Hard', accuracy: stats.by_difficulty.hard?.accuracy || 0, total: stats.by_difficulty.hard?.total || 0 }
    ] : [];

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="analytics-container">
                {/* Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                        Performance Analytics
                    </h1>
                    <p className="text-slate-500 mt-1">Deep dive into your learning patterns and progress</p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="border-slate-200">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Overall Accuracy</p>
                                    <p className="text-3xl font-bold text-slate-900" data-testid="overall-accuracy">
                                        {formatPercentage(stats?.accuracy_percentage || 0)}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                                    <Target className="h-6 w-6 text-indigo-600" />
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-emerald-500" />
                                <span className="text-sm text-emerald-600">{stats?.correct_answers || 0} correct</span>
                                <XCircle className="h-4 w-4 text-red-500 ml-2" />
                                <span className="text-sm text-red-600">{stats?.incorrect_answers || 0} incorrect</span>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Total Questions</p>
                                    <p className="text-3xl font-bold text-slate-900" data-testid="total-attempted">
                                        {stats?.total_questions || 0}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-sky-100 flex items-center justify-center">
                                    <BarChart3 className="h-6 w-6 text-sky-600" />
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-slate-500">
                                Across {SUBJECTS.filter(s => (stats?.by_subject[s]?.total || 0) > 0).length} subjects
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Weak Areas</p>
                                    <p className="text-3xl font-bold text-slate-900" data-testid="weak-areas-count">
                                        {stats?.weak_areas?.length || 0}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-slate-500">
                                Subjects below 70% accuracy
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs defaultValue="subjects" className="space-y-6">
                    <TabsList className="bg-slate-100">
                        <TabsTrigger value="subjects">By Subject</TabsTrigger>
                        <TabsTrigger value="difficulty">By Difficulty</TabsTrigger>
                        <TabsTrigger value="patterns">Missed Patterns</TabsTrigger>
                    </TabsList>

                    {/* By Subject Tab */}
                    <TabsContent value="subjects" className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Radar Chart */}
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle style={{ fontFamily: 'Manrope' }}>Subject Proficiency</CardTitle>
                                    <CardDescription>Your accuracy across all subjects</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {radarData.some(d => d.questions > 0) ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <RadarChart data={radarData}>
                                                <PolarGrid stroke="#E2E8F0" />
                                                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                                                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                                                <Radar
                                                    name="Accuracy"
                                                    dataKey="accuracy"
                                                    stroke="#4F46E5"
                                                    fill="#4F46E5"
                                                    fillOpacity={0.3}
                                                />
                                                <Tooltip 
                                                    content={({ active, payload }) => {
                                                        if (active && payload && payload.length) {
                                                            const data = payload[0].payload;
                                                            return (
                                                                <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
                                                                    <p className="font-medium">{data.fullName}</p>
                                                                    <p className="text-sm text-slate-600">
                                                                        Accuracy: {data.accuracy}%
                                                                    </p>
                                                                    <p className="text-sm text-slate-600">
                                                                        Questions: {data.questions}
                                                                    </p>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                />
                                            </RadarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-[300px] flex items-center justify-center text-slate-500">
                                            <p>No data available yet</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Subject Details */}
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle style={{ fontFamily: 'Manrope' }}>Subject Details</CardTitle>
                                    <CardDescription>Performance breakdown by subject</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {SUBJECTS.map(subject => {
                                        const subjectStats = stats?.by_subject[subject];
                                        const colors = SUBJECT_COLORS[subject];
                                        const total = subjectStats?.total || 0;
                                        const accuracy = subjectStats?.accuracy || 0;
                                        
                                        return (
                                            <div key={subject} className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Badge className={cn(colors.bg, colors.text, colors.border, "text-xs")}>
                                                            {subject}
                                                        </Badge>
                                                        <span className="text-sm text-slate-500">
                                                            {total} questions
                                                        </span>
                                                    </div>
                                                    <span className={cn(
                                                        "font-semibold",
                                                        accuracy >= 70 ? "text-emerald-600" : accuracy >= 50 ? "text-amber-600" : "text-red-600"
                                                    )}>
                                                        {accuracy}%
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className={cn(
                                                            "h-full rounded-full transition-all duration-500",
                                                            accuracy >= 70 ? "bg-emerald-500" : accuracy >= 50 ? "bg-amber-500" : "bg-red-500"
                                                        )}
                                                        style={{ width: `${accuracy}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* By Difficulty Tab */}
                    <TabsContent value="difficulty" className="space-y-6">
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'Manrope' }}>Performance by Difficulty</CardTitle>
                                <CardDescription>How you perform across difficulty levels</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {difficultyData.some(d => d.total > 0) ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={difficultyData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                            <XAxis dataKey="name" />
                                            <YAxis domain={[0, 100]} />
                                            <Tooltip 
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload.length) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
                                                                <p className="font-medium">{data.name}</p>
                                                                <p className="text-sm text-slate-600">
                                                                    Accuracy: {data.accuracy}%
                                                                </p>
                                                                <p className="text-sm text-slate-600">
                                                                    Questions: {data.total}
                                                                </p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Bar 
                                                dataKey="accuracy" 
                                                fill="#4F46E5"
                                                radius={[4, 4, 0, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-[300px] flex items-center justify-center text-slate-500">
                                        <p>No data available yet</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Difficulty Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {difficultyData.map(diff => (
                                <Card key={diff.name} className="border-slate-200">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <Badge className={cn(
                                                diff.name === 'Easy' && "bg-emerald-100 text-emerald-700",
                                                diff.name === 'Medium' && "bg-amber-100 text-amber-700",
                                                diff.name === 'Hard' && "bg-red-100 text-red-700"
                                            )}>
                                                {diff.name}
                                            </Badge>
                                            <span className="text-2xl font-bold text-slate-900">
                                                {diff.accuracy}%
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-500">
                                            {diff.total} questions attempted
                                        </p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* Missed Patterns Tab */}
                    <TabsContent value="patterns" className="space-y-6">
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'Manrope' }}>Missed Question Patterns</CardTitle>
                                <CardDescription>Analysis of your incorrect answers by subject and difficulty</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {patterns && patterns.length > 0 ? (
                                    <div className="space-y-4">
                                        {patterns.map((pattern, index) => {
                                            const colors = SUBJECT_COLORS[pattern.subject];
                                            return (
                                                <div 
                                                    key={pattern.subject}
                                                    className="p-4 rounded-lg bg-slate-50 border border-slate-200"
                                                >
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-2xl font-bold text-slate-300">
                                                                #{index + 1}
                                                            </span>
                                                            <Badge className={cn(colors.bg, colors.text, colors.border)}>
                                                                {pattern.subject}
                                                            </Badge>
                                                        </div>
                                                        <span className="text-lg font-semibold text-red-600">
                                                            {pattern.count} missed
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-4 text-sm text-slate-600">
                                                        <span>Easy: {pattern.difficulty_distribution.easy || 0}</span>
                                                        <span>Medium: {pattern.difficulty_distribution.medium || 0}</span>
                                                        <span>Hard: {pattern.difficulty_distribution.hard || 0}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="h-48 flex items-center justify-center text-slate-500">
                                        <div className="text-center">
                                            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
                                            <p>No missed patterns to analyze yet.</p>
                                            <p className="text-sm">Keep practicing!</p>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
